// The simulated venue's store in a real browser (spec/24 §2.3): `localStorage` for the venue, a `BroadcastChannel` to
// the visitor's other tabs, and the device's clock. Earlier windows' venues are removed as a new one opens.

import { createDemoStore, DEMO_CHANNEL_NAME, demoStorageKey, forgetOtherDemos, type DemoStore, type DemoWindowWire } from '@clavian/demo';

/** The browser's store for one person's window: `localStorage`, and a `BroadcastChannel` to the other tabs. */
export function openBrowserDemoStore(userId: string, window_: DemoWindowWire): DemoStore {
  const key = demoStorageKey(userId, window_.startedAt);
  let storage: Storage;
  try {
    storage = window.localStorage;
    forgetOtherDemos(storage, key);
  } catch {
    // Storage switched off: the venue lives in this tab alone.
    const memory = new Map<string, string>();
    storage = {
      getItem: (name) => memory.get(name) ?? null,
      setItem: (name, value) => void memory.set(name, value),
      removeItem: (name) => void memory.delete(name),
      clear: () => memory.clear(),
      key: (index) => [...memory.keys()][index] ?? null,
      get length() {
        return memory.size;
      },
    };
  }
  const channel = typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(DEMO_CHANNEL_NAME);
  const store = createDemoStore({
    key,
    storage,
    ...(channel === undefined ? {} : { channel }),
    now: () => Date.now(),
  });
  return {
    ...store,
    close: () => {
      store.close();
      channel?.close();
    },
  };
}
